
// CommunityPicsPage.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { db, storage } from "./firebase.js";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

function CommunityPicsPage({ user, isAdmin, isMobile }) {
  const [media, setMedia] = useState([]);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [editId, setEditId] = useState(null);
  const [editCaption, setEditCaption] = useState("");
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [sortBy, setSortBy] = useState('uploadedAtDesc');

  console.log("CommunityPicsPage props:", { user: user?.uid, email: user?.email, isAdmin, isMobile });

  // Fetch and sort media from Firestore
  useEffect(() => {
    const fetchMedia = async () => {
      if (!user) {
        console.log("No authenticated user, skipping fetchMedia");
        setError("Please sign in to view media");
        return;
      }
      try {
        console.log("Fetching community_photos for user:", user.uid, "from database: nmrdb");
        const snapshot = await getDocs(collection(db, "community_photos"));
        console.log("Fetched snapshot, docs:", snapshot.docs.length);
        let mediaList = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            uploaderName: data.uploadedBy,
          };
        });
        // Sort media
        const sortMedia = (list) => {
          return [...list].sort((a, b) => {
            if (sortBy === 'uploadedAtDesc') return new Date(b.uploadedAt) - new Date(a.uploadedAt);
            if (sortBy === 'uploadedAtAsc') return new Date(a.uploadedAt) - new Date(b.uploadedAt);
            if (sortBy === 'captionAsc') return a.caption.localeCompare(b.caption);
            return 0;
          });
        };
        mediaList = sortMedia(mediaList);
        console.log("Media list:", mediaList);
        setMedia(mediaList);
        setError(null);
      } catch (err) {
        console.error("Error fetching media:", err, "Code:", err.code, "Message:", err.message);
        setError("Failed to load media: " + err.message);
      }
    };
    fetchMedia();
  }, [user, sortBy]);

  // Handle file selection
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  // Handle adding new media
  const handleAddMedia = async () => {
    if (!file || !caption) {
      setError("Please provide a file and caption");
      return;
    }
    if (!user) {
      setError("Please sign in to upload media");
      return;
    }
    if (!user.email) {
      setError("User email is not available");
      return;
    }
    setIsUploading(true);
    try {
      console.log("Starting upload for file:", file.name, "by user:", user.uid, "email:", user.email);
      const metadata = { customMetadata: { uploadedBy: user.uid } };
      const fileRef = ref(storage, `CommunityPhotos/${Date.now()}_${file.name}`);
      console.log("Uploading to Storage path:", fileRef.fullPath, "with metadata:", JSON.stringify(metadata, null, 2));
      await uploadBytes(fileRef, file, metadata);
      console.log("File uploaded successfully");
      const url = await getDownloadURL(fileRef);
      console.log("Download URL:", url);

      console.log("Creating Firestore document in community_photos (nmrdb)...");
      const docData = {
        url,
        caption,
        uploadedBy: user.email,
        uploaderUid: user.uid,
        uploadedAt: new Date().toISOString(),
      };
      console.log("Firestore document data:", JSON.stringify(docData, null, 2));
      const docRef = await addDoc(collection(db, "community_photos"), docData);
      console.log("Firestore document created with ID:", docRef.id);

      const newMedia = [...media, { id: docRef.id, ...docData, uploaderName: user.email }];
      const sortMedia = (list) => {
        return [...list].sort((a, b) => {
          if (sortBy === 'uploadedAtDesc') return new Date(b.uploadedAt) - new Date(a.uploadedAt);
          if (sortBy === 'uploadedAtAsc') return new Date(a.uploadedAt) - new Date(b.uploadedAt);
          if (sortBy === 'captionAsc') return a.caption.localeCompare(b.caption);
          return 0;
        });
      };
      setMedia(sortMedia(newMedia));
      setFile(null);
      setCaption("");
      setError(null);
    } catch (err) {
      console.error("Error adding media:", err, "Code:", err.code, "Message:", err.message);
      setError("Failed to upload media: " + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle editing media
  const handleEditMedia = async (id) => {
    try {
      console.log("Editing media ID:", id, "new caption:", editCaption);
      const docRef = doc(db, "community_photos", id);
      await updateDoc(docRef, { caption: editCaption });
      const updatedMedia = media.map((item) => (item.id === id ? { ...item, caption: editCaption } : item));
      const sortMedia = (list) => {
        return [...list].sort((a, b) => {
          if (sortBy === 'uploadedAtDesc') return new Date(b.uploadedAt) - new Date(a.uploadedAt);
          if (sortBy === 'uploadedAtAsc') return new Date(a.uploadedAt) - new Date(b.uploadedAt);
          if (sortBy === 'captionAsc') return a.caption.localeCompare(b.caption);
          return 0;
        });
      };
      setMedia(sortMedia(updatedMedia));
      setEditId(null);
      setEditCaption("");
      setError(null);
    } catch (err) {
      console.error("Error editing media:", err, "Code:", err.code, "Message:", err.message);
      setError("Failed to edit media: " + err.message);
    }
  };

  // Handle deleting media
  const handleDeleteMedia = async (id, url) => {
    try {
      console.log("Deleting media ID:", id, "URL:", url);
      const fileRef = ref(storage, url);
      await deleteObject(fileRef);
      await deleteDoc(doc(db, "community_photos", id));
      const updatedMedia = media.filter((item) => item.id !== id);
      const sortMedia = (list) => {
        return [...list].sort((a, b) => {
          if (sortBy === 'uploadedAtDesc') return new Date(b.uploadedAt) - new Date(a.uploadedAt);
          if (sortBy === 'uploadedAtAsc') return new Date(a.uploadedAt) - new Date(b.uploadedAt);
          if (sortBy === 'captionAsc') return a.caption.localeCompare(b.caption);
          return 0;
        });
      };
      setMedia(sortMedia(updatedMedia));
      setError(null);
    } catch (err) {
      console.error("Error deleting media:", err, "Code:", err.code, "Message:", err.message);
      setError("Failed to delete media: " + err.message);
    }
  };

  return (
    <div className={`community-pics-container ${isMobile ? "community-pics-mobile" : ""}`}>
      <h2 className="community-pics-title">Community Photos & Videos</h2>
      {error && <p className="text-red-500">{error}</p>}

      {/* Upload Form */}
      <div className="upload-section">
        <input
          type="file"
          accept="image/*,video/*"
          onChange={handleFileChange}
          className="modal-input"
          disabled={isUploading}
        />
        <input
          type="text"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Enter caption"
          className="modal-input"
          disabled={isUploading}
        />
        <button
          onClick={handleAddMedia}
          className="modal-button modal-button-primary"
          disabled={isUploading}
        >
          {isUploading ? "Uploading..." : "Upload Media"}
        </button>
      </div>

      {/* Sorting Dropdown */}
      <div className="sort-section">
        <label htmlFor="sortBy" className="sort-label">Sort by: </label>
        <select
          id="sortBy"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="modal-input sort-select"
        >
          <option value="uploadedAtDesc">Newest First</option>
          <option value="uploadedAtAsc">Oldest First</option>
          <option value="captionAsc">Caption A-Z</option>
        </select>
      </div>

      {/* Media Grid */}
      <div className="media-grid">
        {media.length > 0 ? (
          media.map((item) => (
            <div key={item.id} className="media-item">
              <Link to={`/community-pics/${item.id}`}>
                {item.url.includes("video") ? (
                  <video src={item.url} className="media-content" />
                ) : (
                  <img src={item.url} alt={item.caption} className="media-content" />
                )}
              </Link>
              <p className="media-caption">{item.caption}</p>
              <p className="media-uploader">Uploaded by: {item.uploaderName}</p>
              <p className="media-date">
                Uploaded: {new Date(item.uploadedAt).toLocaleDateString()}
              </p>
              {(isAdmin || item.uploaderUid === user?.uid) && (
                <div className="media-actions">
                  <button
                    onClick={() => {
                      setEditId(item.id);
                      setEditCaption(item.caption);
                    }}
                    className="modal-button modal-button-secondary"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteMedia(item.id, item.url)}
                    className="modal-button modal-button-danger"
                  >
                    Delete
                  </button>
                </div>
              )}
              {editId === item.id && (
                <div className="edit-section">
                  <input
                    type="text"
                    value={editCaption}
                    onChange={(e) => setEditCaption(e.target.value)}
                    className="modal-input"
                  />
                  <button
                    onClick={() => handleEditMedia(item.id)}
                    className="modal-button modal-button-primary"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditId(null)}
                    className="modal-button modal-button-secondary"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          ))
        ) : (
          <p>No media available</p>
        )}
      </div>

      <style>
        {`
          .community-pics-container {
            padding: 2rem;
            background: white;
            border-radius: 0.5rem;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            margin: 2rem auto;
            max-width: 1200px;
            opacity: 0.9;
          }
          .community-pics-title {
            font-size: 1.5rem;
            margin-bottom: 1rem;
            text-align: center;
          }
          .upload-section {
            display: flex;
            flex-wrap: wrap;
            gap: 1rem;
            margin-bottom: 1rem;
            justify-content: center;
          }
          .sort-section {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 1rem;
            gap: 0.5rem;
          }
          .sort-label {
            font-size: 1rem;
            color: #4b5563;
          }
          .sort-select {
            padding: 0.5rem;
            border: 1px solid #ddd;
            border-radius: 0.25rem;
            font-size: 1rem;
            max-width: 200px;
          }
          .media-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
            gap: 1rem;
          }
          .media-item {
            border: 1px solid #ddd;
            border-radius: 0.25rem;
            padding: 1rem;
            text-align: center;
          }
          .media-content {
            max-width: 100%;
            max-height: 200px;
            object-fit: cover;
            border-radius: 0.25rem;
            margin-bottom: 0.5rem;
            cursor: pointer;
          }
          .media-caption {
            font-size: 1rem;
            margin: 0.5rem 0;
            font-weight: bold;
          }
          .media-uploader {
            font-size: 0.875rem;
            margin: 0.25rem 0;
            color: #4b5563;
          }
          .media-date {
            font-size: 0.875rem;
            color: #6b7280;
            margin-bottom: 0.5rem;
          }
          .media-actions {
            display: flex;
            justify-content: center;
            gap: 0.5rem;
          }
          .edit-section {
            margin-top: 1rem;
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            justify-content: center;
          }
          .modal-input {
            width: 100%;
            padding: 0.5rem;
            margin-bottom: 1rem;
            border: 1px solid #ddd;
            border-radius: 0.25rem;
            font-size: 1rem;
            max-width: 300px;
          }
          .modal-button {
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
            border: none;
            cursor: pointer;
            font-size: 1rem;
            margin: 0 0.25rem;
          }
          .modal-button-primary {
            background: #007bff;
            color: white;
          }
          .modal-button-primary:hover {
            background: #0056b3;
          }
          .modal-button-secondary {
            background: #6c757d;
            color: white;
          }
          .modal-button-secondary:hover {
            background: #5a6268;
          }
          .modal-button-danger {
            background: #dc3545;
            color: white;
          }
          .modal-button-danger:hover {
            background: #c82333;
          }
          @media (max-width: 640px) {
            .community-pics-mobile {
              padding: 1rem;
              margin: 1rem;
            }
            .community-pics-title {
              font-size: 1.25rem;
            }
            .upload-section {
              flex-direction: column;
              align-items: stretch;
            }
            .sort-section {
              flex-direction: column;
              align-items: stretch;
            }
            .sort-select {
              max-width: 100%;
            }
            .media-grid {
              grid-template-columns: 1fr;
            }
            .media-content {
              max-height: 150px;
            }
            .media-caption {
              font-size: 0.9rem;
            }
            .media-uploader {
              font-size: 0.75rem;
            }
            .media-date {
              font-size: 0.75rem;
            }
            .modal-input {
              max-width: 100%;
            }
          }
        `}
      </style>
    </div>
  );
}

export default CommunityPicsPage;